<script lang="ts" setup>
import { format } from 'date-fns'
import type { MemberPageConfig } from '../configs/types'
import { useUserStore } from '@/stores'
import { useSubscription } from '@/composables/payment'
import { tripartiteLoginPage } from '@/composables/useTripartiteLogin'
import { useElementClick } from '@/composables/tracks/useElementClick'
import { prefixCDN } from '@/utils/cdn'

interface Props {
  config: MemberPageConfig
}

defineProps<Props>()

const userStore = useUserStore()
const { elementClick } = useElementClick()
const { subscribeProducts } = useSubscription('member')

function login() {
  elementClick('login')
  tripartiteLoginPage('Profile')
}
</script>

<template>
  <div class="page-cover">
    <!-- 品牌行 -->
    <div class="brand-row flex items-center px-15px">
      <img src="/images/vowpath/home/logo.png" class="brand-logo" alt="Vowpath">
    </div>
    <!-- 头像 -->
    <div class="px-20px pt-16px pb-24px flex items-center justify-between">
      <div class="shrink-0">
        <img v-if="userStore.$state.headImageUrl" class="w-66px h-66px rounded-full avatar-ring" :src="prefixCDN(userStore.$state.thirdHeadImgUrl)" alt="avatar">
        <!-- 默认头像切图自带渐变底与描边 -->
        <img v-else class="w-66px h-66px" src="/images/vowpath/member/avatar-default.png" alt="avatar">
      </div>

      <div class="flex-1 overflow-hidden px-12px">
        <div class="flex items-center width-full">
          <div class="username overflow-hidden whitespace-nowrap text-ellipsis">
            {{ userStore.$state.thirdNickName || userStore.$state.email || userStore.$state.memberName }}
          </div>
        </div>
        <p class="uid">
          UID {{ userStore.$state.code }}
        </p>
        <!-- Diamond Card 生效态：横幅隐藏，UID 下方展示钻石卡图标 + 到期日（对照蓝湖「个人中心(已订阅)」） -->
        <div v-if="userStore.$state.subscribe && userStore.$state.subscribeEndDate" class="dc-expires flex items-center">
          <img src="/images/vowpath/welfare/dc-card-icon.png" class="dc-expires-icon shrink-0" alt="">
          <span>{{ config.texts.expires }} {{ format(new Date(userStore.$state.subscribeEndDate), 'yyyy-MM-dd') }}</span>
        </div>
      </div>

      <div v-if="!userStore.$state.thirdPartType" type="link" class="shrink-0">
        <a class="flex items-center text-15px sign-in-link" @click="login">
          {{ config.texts.signIn }}
          <i-icon-park-outline-right class="ml-2px" />
        </a>
      </div>
    </div>

    <!-- Diamond Card 订阅入口（订阅生效后整块隐藏，生效标识见头像信息区） -->
    <div v-if="!userStore.$state.subscribe && subscribeProducts?.length" class="vip-cover mx-10px px-16px">
      <div class="flex items-center justify-between">
        <div class="vip-title flex-1 min-w-0">
          {{ config.texts.getVip }}
        </div>
        <router-link :to="{ name: 'subscribe' }" class="shrink-0">
          <div class="details-btn flex items-center justify-center" @click="elementClick('Details')">
            {{ config.texts.details }}
          </div>
        </router-link>
      </div>
      <div class="vip-subtitle">
        {{ config.texts.moreCoinsMoreBenefits }}
      </div>
    </div>

    <!-- 金币余额卡片 -->
    <!-- v-if="userStore.$state.thirdPartType"  -->
    <div class="coin-card mx-10px px-16px flex items-center justify-between">
      <div class="flex items-center">
        <img src="/images/vowpath/home/icon-diamond.png" class="coin-diamond shrink-0" alt="">
        <span class="coin-balance-text">{{ userStore.coinBalance }}</span>
      </div>
      <router-link :to="{ name: 'pay' }">
        <div class="top-up-btn flex items-center justify-center" @click="elementClick('TopUp')">
          Top Up
        </div>
      </router-link>
    </div>

    <!-- setting -->
    <div class="menu-card mt-10px mx-10px px-20px">
      <div>
        <router-link
          :to="{ name: 'recharge' }"
          class="w-full h-61px flex items-center justify-between"
        >
          <div class="flex items-center space-x-7px">
            <img src="/images/vowpath/member/icon-transaction-new.png" width="21px" height="21px" alt="">
            <p class="text-n-text-1">
              {{ config.texts.transactionHistory }}
            </p>
          </div>
          <i-icon-park-outline-right class="text-n-text-3" />
        </router-link>
      </div>
      <div>
        <router-link
          :to="{ name: 'consume' }"
          class="w-full h-61px flex items-center justify-between border-t border-n-border-primary"
        >
          <div class="flex items-center space-x-7px">
            <img src="/images/vowpath/member/icon-unlock-new.png" width="21px" height="21px" alt="">
            <p class="text-n-text-1">
              {{ config.texts.chaptersUnlocked }}
            </p>
          </div>
          <i-icon-park-outline-right class="text-n-text-3" />
        </router-link>
      </div>
      <div>
        <router-link :to="{ name: 'contact-us' }">
          <div class="w-full h-61px flex items-center justify-between border-t border-n-border-primary" @click="elementClick('contact_us')">
            <div class="flex items-center space-x-7px">
              <img src="/images/vowpath/member/icon-contact-new.png" width="21px" height="21px" alt="">
              <p class="text-n-text-1">
                {{ config.texts.contactUs }}
              </p>
            </div>
            <i-icon-park-outline-right class="text-n-text-3" />
          </div>
        </router-link>
      </div>
      <div>
        <router-link :to="{ name: 'setting' }">
          <div class="w-full h-61px flex items-center justify-between border-t border-n-border-primary" @click="elementClick('setting')">
            <div class="flex items-center space-x-7px">
              <img src="/images/vowpath/member/icon-setting-new.png" width="21px" height="21px" alt="">
              <p class="text-n-text-1">
                {{ config.texts.setting }}
              </p>
            </div>
            <i-icon-park-outline-right class="text-n-text-3" />
          </div>
        </router-link>
      </div>
    </div>

    <BasicTabbar tab="member" />
  </div>
</template>

<style scoped>
.page-cover {
  width: 100%;
  min-height: 100vh;
  background: #20214b;
}

.brand-row {
  height: 44px;
}

.brand-logo {
  height: 25px;
  width: auto;
}

/* 头像：渐变紫底 + 半透明粉紫描边（对照蓝湖 66px 圆） */
.avatar-ring {
  background: linear-gradient(0deg, rgba(70, 49, 116, 1) 0%, rgba(73, 45, 96, 1) 100%);
  border: 2px solid rgba(214, 103, 230, 0.3);
}

.sign-in-link {
  color: #7b7994;
}

/* Diamond Card 横幅：蓝湖「VIP背景」切图（355x80，渐变 + 右侧装饰钻石） */
.vip-cover {
  width: 355px;
  min-height: 80px;
  padding-top: 14px;
  padding-bottom: 15px;
  background: url('/images/vowpath/member/dc-banner-bg.png') no-repeat center / 100% 100%;
  border-radius: 8px;
  box-sizing: border-box;
}

.vip-title {
  font-weight: 600;
  font-size: 18px;
  line-height: 22px;
  color: white;
}

/* 副标题：单行展示，字号略小于设计稿 12px 以适配窄屏 */
.vip-subtitle {
  font-weight: 400;
  font-size: 11px;
  line-height: 16px;
  color: #EFDCFF;
  margin-top: 3px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Details：白底粉字胶囊，1px 淡粉描边 */
.details-btn {
  width: 80px;
  height: 32px;
  background: #ffffff;
  border: 1px solid rgba(238, 164, 255, 0.56);
  border-radius: 20px;
  font-weight: 500;
  font-size: 14px;
  color: #ff5acd;
}

/* 钻石卡：蓝湖「卡片背景」切图（355x60，渐变 + 右侧斜纹底纹，描边已含在切图内） */
.coin-card {
  height: 60px;
  background: url('/images/vowpath/member/topup-card-bg.png') no-repeat center / 100% 100%;
  border-radius: 8px;
  padding: 14px 16px;
  box-sizing: border-box;
  position: relative;
  margin-top: 10px;
}

.coin-diamond {
  width: 31px;
  height: 27px;
  margin-right: 8px;
}

.coin-balance-text {
  font-size: 16px;
  font-family: HelveticaNeue-Medium, sans-serif;
  font-weight: 500;
  color: #ffffff;
}

/* Top Up 按钮：浅粉紫渐变 + 半透明白描边 */
.top-up-btn {
  height: 32px;
  border-radius: 18px;
  padding: 0 15px;
  background: linear-gradient(270deg, rgba(255, 132, 218, 1) 0%, rgba(202, 101, 250, 1) 46%, rgba(168, 91, 253, 1) 100%);
  border: 1px solid rgba(255, 255, 255, 0.19);
  font-size: 14px;
  font-family: Inter-Medium, sans-serif;
  font-weight: 500;
  color: #fff;
}

/* 菜单卡：深底描边 */
.menu-card {
  background: rgba(39, 40, 88, 1);
  border: 1px solid rgba(66, 66, 109, 1);
  border-radius: 8px;
}

.username {
  height: 18px;
  font-weight: 500;
  font-size: 18px;
  color: #ffffff;
  line-height: 18px;
}

.uid {
  font-weight: 400;
  font-size: 12px;
  color: #7b7994;
  line-height: 11px;
  margin-top: 8px;
}

/* Diamond Card 订阅到期日：24x15 钻石卡图标 + 12px 灰紫文字，与 UID 同色系 */
.dc-expires {
  margin-top: 9px;
  font-weight: 400;
  font-size: 12px;
  line-height: 15px;
  color: #8382a6;
}

.dc-expires-icon {
  width: 24px;
  height: 15px;
  margin-right: 6px;
}
</style>
